import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../apiBase';

const cardStyle = { background: '#fff', borderRadius: '12px', border: '1px solid var(--sp-gray-100)', padding: '16px', marginTop: '14px' };
const primaryBtn = { background: '#2563eb', color: '#fff', border: 'none', borderRadius: '9px', padding: '10px 16px', cursor: 'pointer', fontWeight: 600, transition: 'background 0.2s' };
const savedBtn = { ...primaryBtn, background: '#16a34a' };
const rowStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 };

/**
 * СКУД, Фаза 2 — минимальные настройки помещения + создание киоск-учёток.
 * Полноценная страница оборудования (принтер как PeripheralDevice) — Фаза 4,
 * здесь достаточно, чтобы можно было включить СКУД и реально протестировать сканер.
 */
export default function SkudVenueSettings({ venueId }) {
    const token = localStorage.getItem('token');
    const api = useMemo(() => axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    const [skudEnabled, setSkudEnabled] = useState(false);
    const [requireWristband, setRequireWristband] = useState(false);
    const [wristbandPrintMode, setWristbandPrintMode] = useState('none');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');
    const [kiosks, setKiosks] = useState([]);
    const [newKiosk, setNewKiosk] = useState(null);
    const savedTimerRef = useRef(null);

    // Любое изменение настройки после успешного сохранения — сразу возвращаем кнопку в обычный вид,
    // чтобы не вводить в заблуждение "сохранено", когда на экране уже другие значения.
    function markDirty() {
        if (saved) {
            setSaved(false);
            clearTimeout(savedTimerRef.current);
        }
    }

    async function loadKiosks() {
        try {
            const res = await api.get('/kiosk-accounts', { params: { venueId } });
            setKiosks(res.data || []);
        } catch {
            // молча — не критично для основной страницы
        }
    }

    async function loadRoomSettings() {
        try {
            const res = await api.get(`/room/${venueId}`);
            setSkudEnabled(!!res.data.skudEnabled);
            setRequireWristband(!!res.data.requireWristband);
            setWristbandPrintMode(res.data.wristbandPrintMode || 'none');
        } catch {
            // молча — форма просто останется на дефолтах
        }
    }

    useEffect(() => {
        loadRoomSettings();
        loadKiosks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venueId]);

    async function saveSettings() {
        setSaving(true);
        setSaved(false);
        setError('');
        try {
            await api.put(`/room/${venueId}/skud-settings`, {
                skudEnabled,
                requireWristband,
                wristbandPrintMode,
                printerDeviceId: null,
            });
            setSaved(true);
            clearTimeout(savedTimerRef.current);
            savedTimerRef.current = setTimeout(() => setSaved(false), 5000);
        } catch (e) {
            setError(e.response?.data?.message || 'Не удалось сохранить настройки СКУД');
        }
        setSaving(false);
    }

    useEffect(() => () => clearTimeout(savedTimerRef.current), []);

    async function createKiosk() {
        setError('');
        try {
            const res = await api.post('/kiosk-accounts', { venueId });
            setNewKiosk(res.data);
            await loadKiosks();
        } catch (e) {
            setError(e.response?.data?.message || 'Не удалось создать учётку сканера');
        }
    }

    async function resetPassword(userId) {
        try {
            const res = await api.post(`/kiosk-accounts/${userId}/reset-password`);
            setNewKiosk({ userId, username: kiosks.find(k => k.id === userId)?.username, password: res.data.password });
        } catch (e) {
            setError(e.response?.data?.message || 'Не удалось сбросить пароль');
        }
    }

    return (
        <div style={cardStyle}>
            <h3 style={{ margin: '0 0 12px' }}>СКУД (Фаза 2 — тест)</h3>
            {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

            <label style={rowStyle}>
                <input type="checkbox" checked={skudEnabled} onChange={e => { markDirty(); setSkudEnabled(e.target.checked); }} />
                Включить СКУД в этом помещении
            </label>
            <label style={rowStyle}>
                <input type="checkbox" checked={requireWristband} onChange={e => { markDirty(); setRequireWristband(e.target.checked); }} />
                Выдавать браслет при входе
            </label>
            {requireWristband && (
                <label style={rowStyle}>
                    Режим печати:
                    <select value={wristbandPrintMode} onChange={e => { markDirty(); setWristbandPrintMode(e.target.value); }}>
                        <option value="none">Ничего не печатать (лента уже нужного цвета)</option>
                        <option value="blank_dispense">Просто выдать ленту</option>
                        <option value="name_date">Печатать имя и дату</option>
                    </select>
                </label>
            )}
            <button type="button" style={saved ? savedBtn : primaryBtn} onClick={saveSettings} disabled={saving}>
                {saving ? '…' : saved ? '✓ Сохранено' : 'Сохранить настройки СКУД'}
            </button>

            <hr style={{ margin: '18px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

            <h4 style={{ margin: '0 0 10px' }}>Учётки сканера (планшет/ПК на входе)</h4>
            {kiosks.length === 0 && <p style={{ color: '#64748b', fontSize: 13 }}>Учёток пока нет</p>}
            {kiosks.map(k => (
                <div key={k.id} style={{ ...rowStyle, justifyContent: 'space-between' }}>
                    <span>{k.username}</span>
                    <button type="button" onClick={() => resetPassword(k.id)}>Сбросить пароль</button>
                </div>
            ))}
            <button type="button" style={{ ...primaryBtn, marginTop: 8 }} onClick={createKiosk}>
                + Создать учётку сканера
            </button>

            {newKiosk && (
                <div style={{ marginTop: 14, padding: 12, background: '#fef9c3', borderRadius: 8, fontSize: 14 }}>
                    <b>Логин и пароль показаны один раз — сохраните их:</b>
                    <div>Логин: <code>{newKiosk.username}</code></div>
                    <div>Пароль: <code>{newKiosk.password}</code></div>
                    <div style={{ marginTop: 6, color: '#92400e' }}>
                        Введите их на планшете/ПК на странице входа, затем откройте /skud/kiosk
                    </div>
                </div>
            )}
        </div>
    );
}
